import { useMemo } from "react";
import { createNestedMapping } from "helpers";
import { MaterialChemistry } from "store/api/generatedApi";
import {
  SearchChemicalElements,
  SearchMaterialChemistry,
} from "contexts/search/context";
import { ChemistryEditorMaterials, MaterialOverrideRow } from "./types";
import { orderByMetadata } from "./chemistryMetadata";

export interface UseMaterialOverrideRowsArgs {
  materials: ChemistryEditorMaterials;
  chemicalElements: SearchChemicalElements;
  materialChemistry: SearchMaterialChemistry;
  materialChemistryOverrides: MaterialChemistry[];
  selectedChemicalElementId: number | null;
  t: (key: string) => string;
}

export const buildMaterialOverrideRows = ({
  materials,
  chemicalElements,
  materialChemistry,
  materialChemistryOverrides,
  selectedChemicalElementId,
  t,
}: UseMaterialOverrideRowsArgs): MaterialOverrideRow[] => {
  const overrides = createNestedMapping(
    materialChemistryOverrides,
    (measurement) => measurement.material_id,
    (measurement) => measurement.chemical_element_id
  );
  const elements = orderByMetadata(chemicalElements.byIndex).filter(
    (element) =>
      selectedChemicalElementId === null ||
      element.id === selectedChemicalElementId
  );
  return orderByMetadata(materials.byIndex).flatMap((material) =>
    elements.flatMap((element) => {
      const original = materialChemistry[material.id]?.[element.id];
      const override = overrides[material.id]?.[element.id];
      const measurement = override ?? original;
      if (!measurement) return [];
      return [
        {
          ...measurement,
          material_name: material.name,
          chemical_element_name: `${element.symbol} (${t(element.name)})`,
          is_overridden: override !== undefined,
          is_loc_overridden:
            override !== undefined && override.loc !== original?.loc,
          is_scale_overridden:
            override !== undefined && override.scale !== original?.scale,
        },
      ];
    })
  );
};

export const useMaterialOverrideRows = (
  args: UseMaterialOverrideRowsArgs
): MaterialOverrideRow[] => {
  const {
    materials,
    chemicalElements,
    materialChemistry,
    materialChemistryOverrides,
    selectedChemicalElementId,
    t,
  } = args;
  return useMemo(
    () =>
      buildMaterialOverrideRows({
        materials,
        chemicalElements,
        materialChemistry,
        materialChemistryOverrides,
        selectedChemicalElementId,
        t,
      }),
    [
      materials,
      chemicalElements,
      materialChemistry,
      materialChemistryOverrides,
      selectedChemicalElementId,
      t,
    ]
  );
};
